import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Upload, LogOut, FileText, DollarSign, List, Receipt, Package, MapPin, ChevronDown, ChevronRight, Globe, Flame, History, Star, ShieldCheck, Menu, X as XIcon, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";
import { useState, useEffect } from "react";

const Layout = ({ setAuth }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState({
    createInvoice: false,
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sectionFromPath = (path) => {
    if (path.startsWith('/invoice-c/create')) return 'invoices';
    if (path === '/invoice-c/history' || path === '/invoice-c/import-legacy') return 'history';
    if (path.startsWith('/invoice-c/report/')) return 'report';
    if (path.startsWith('/invoice-c/overweight')) return 'overweight';
    if (path.startsWith('/invoice-c/customers/')) return 'customers';
    if (path.startsWith('/invoice-c/pricelist/') || path === '/invoice-c/stations' || path === '/invoice-c/fsc-settings') return 'pricelist';
    if (path.startsWith('/invoice-c/sales/')) return 'sales';
    return null;
  };
  const [activeSection, setActiveSection] = useState(() => sectionFromPath(location.pathname));

  useEffect(() => {
    setActiveSection(sectionFromPath(location.pathname));
    if (location.pathname.startsWith('/invoice-c/create/')) {
      setExpandedMenus(prev => ({ ...prev, createInvoice: true }));
    }
  }, [location.pathname]);

  const sectionButton = (key, title) => (
    <button
      type="button"
      aria-expanded={activeSection === key}
      onClick={() => setActiveSection(prev => prev === key ? null : key)}
      className="w-full flex items-center justify-between px-3 py-2.5 mt-3 rounded-xl text-xs font-black uppercase tracking-widest text-muted-foreground hover:bg-muted/60"
    >
      <span>{title}</span>
      {activeSection === key ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
    </button>
  );

  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUserData(JSON.parse(storedUser));
      } catch (e) {
        console.error("Gagal membaca data user", e);
      }
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuth(false);
    navigate('/login');
  };

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const isActive = (path) => location.pathname === path;
  const isParentActive = (paths) => paths.some(path => location.pathname.startsWith(path));

  const checkAccess = (menuName) => {
    if (!userData) return false;
    if (userData.role === "ADMIN") return true;
    
    let userPermissions = [];
    try {
        userPermissions = JSON.parse(userData.permissions || "[]"); 
    } catch(e) {
        userPermissions = Array.isArray(userData.permissions) ? userData.permissions : [];
    }

    return userPermissions.includes(menuName);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50/80 via-purple-50/40 to-pink-50/60">
      {/* Hamburger button - mobile only */}
      <button onClick={() => setSidebarOpen(true)}
        className="fixed top-4 left-4 z-50 md:hidden bg-white shadow-lg rounded-xl p-2.5 border border-gray-200">
        <Menu size={20} className="text-gray-700" />
      </button>

      {/* Overlay - mobile only */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex">
        {/* Floating Sidebar */}
        <aside className={`w-64 min-h-screen p-4 fixed md:static z-50 top-0 left-0 h-full transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
          <div className="sticky top-4 bg-white/90 backdrop-blur-lg rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-border/50 p-6 flex flex-col h-[calc(100vh-2rem)]">
            
            {/* Tombol close - mobile only */}
            <button onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 md:hidden text-gray-400 hover:text-gray-700">
              <XIcon size={20} />
            </button>
            {/* Logo & Info User */}
            <div className="mb-6">
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center justify-center">
                  <img src="/login-assets/images/bluee.png" alt="BlueBill" style={{ height: '52px', maxWidth: '180px', objectFit: 'contain' }} />
                </div>
                <h1 className="text-2xl font-heading font-bold whitespace-nowrap" style={{ color: '#135CCB' }}>
                  INVOICE C
                </h1>
              </div>
              {userData && (
                <div className="mt-4 px-3 py-2 bg-slate-50 rounded-lg border border-slate-100">
                    <p className="text-xs font-bold text-slate-800 truncate">{userData.name}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                        <ShieldCheck size={12} className={userData.role === 'ADMIN' ? 'text-purple-600' : 'text-blue-500'}/>
                        <p className="text-[10px] font-mono text-slate-500">{userData.role}</p>
                    </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-1 overflow-y-auto pr-2 custom-scrollbar">
              
              {/* Dashboard - SELALU TERBUKA */}
              <Link
                to="/invoice-c/dashboard"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${
                  isActive('/invoice-c/dashboard') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg shadow-primary/25' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'
                }`}
              >
                <LayoutDashboard className="w-5 h-5" />
                <span>Dashboard</span>
              </Link>

              {/* INVOICES Section */}
              {(checkAccess('Invoice Export') || checkAccess('Invoice Import') || checkAccess('Invoice Domestic')) && (
                <>
                  {sectionButton('invoices', 'Invoices')}
                  {activeSection === 'invoices' && (
                    <>
                  
                  {/* Create Invoice - Collapsible */}
                  <div>
                    <button
                      onClick={() => toggleMenu('createInvoice')}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${
                        isParentActive(['/invoice-c/create']) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted/60'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Receipt className="w-5 h-5" />
                        <span>Create Invoice</span>
                      </div>
                      {expandedMenus.createInvoice ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    
                    {/* Submenu Create Invoice */}
                    {expandedMenus.createInvoice && (
                      <div className="ml-8 mt-1 space-y-1">
                        {checkAccess('Invoice Export') && (
                            <Link to="/invoice-c/create/international" className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive('/invoice-c/create/international') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-md' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-current" /><span>Export</span>
                            </Link>
                        )}
                        {checkAccess('Invoice Domestic') && (
                            <Link to="/invoice-c/create/domestic" className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive('/invoice-c/create/domestic') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-md' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-current" /><span>Domestic</span>
                            </Link>
                        )}
                        {checkAccess('Invoice Import') && (
                            <Link to="/invoice-c/create/gimp" className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive('/invoice-c/create/gimp') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-md' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-current" /><span>Import (GIMP)</span>
                            </Link>
                        )}
                      </div>
                    )}
                  </div>
                    </>
                  )}
                </>
              )}

              {/* HISTORY Section */}
              {checkAccess('Invoice History') && (
                <>
                  {sectionButton('history', 'History & Approval')}
                  {activeSection === 'history' && (
                    <>
                  <Link to="/invoice-c/history" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/history') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                    <History className="w-5 h-5" /><span>Invoice History</span>
                  </Link>
                  {userData.role === 'ADMIN' && (
                    <Link to="/invoice-c/import-legacy" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/import-legacy') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                      <Upload className="w-5 h-5" /><span>Import Invoice C</span>
                    </Link>
                  )}
                    </>
                  )}
                </>
              )}

              {/* REPORT Section */}
              {(checkAccess('Report') || checkAccess('Faktur Pajak Report') || checkAccess('Gyakuso Report') || checkAccess('Tanda Terima Report')) && (
                <>
                  {sectionButton('report', 'Report')}
                  {activeSection === 'report' && (
                    <>
                      {checkAccess('Report') && (
                        <Link to="/invoice-c/report/myob" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/report/myob') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                          <FileText className="w-5 h-5" /><span>Export MYOB</span>
                        </Link>
                      )}
                      {checkAccess('Faktur Pajak Report') && (
                        <Link to="/invoice-c/report/faktur-pajak" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/report/faktur-pajak') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                          <FileText className="w-5 h-5" /><span>Faktur Pajak</span>
                        </Link>
                      )}
                      {checkAccess('Gyakuso Report') && (
                        <Link to="/invoice-c/report/gyakuso" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/report/gyakuso') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                          <FileText className="w-5 h-5" /><span>Gyakuso</span>
                        </Link>
                      )}
                      {checkAccess('Tanda Terima Report') && (
                        <Link to="/invoice-c/report/receipt-list" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/report/receipt-list') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                          <FileText className="w-5 h-5" /><span>List Tanda Terima</span>
                        </Link>
                      )}
                    </>
                  )}
                </>
              )}

              {/* OVERWEIGHT Section */}
              {checkAccess('Overweight') && (
                <>
                  {sectionButton('overweight', 'Overweight')}
                  {activeSection === 'overweight' && (
                    <>
                  <Link to="/invoice-c/overweight" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/overweight') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                    <Package className="w-5 h-5" /><span>Overweight</span>
                  </Link>
                    </>
                  )}
                </>
              )}

              {/* CUSTOMERS Section */}
              {(checkAccess('Add Customer') || checkAccess('Import Data') || checkAccess('Customers List')) && (
                <>
                  {sectionButton('customers', 'Customers')}
                  {activeSection === 'customers' && (
                    <>
                  {checkAccess('Add Customer') && (
                      <Link to="/invoice-c/customers/add" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/customers/add') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                        <Users className="w-5 h-5" /><span>Add Customer</span>
                      </Link>
                  )}
                  {checkAccess('Import Data') && (
                      <Link to="/invoice-c/customers/import" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/customers/import') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                        <Upload className="w-5 h-5" /><span>Import Data</span>
                      </Link>
                  )}
                  {checkAccess('Customers List') && (
                      <Link to="/invoice-c/customers/list" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/customers/list') || (location.pathname.startsWith('/invoice-c/customers/') && location.pathname !== '/invoice-c/customers/add' && location.pathname !== '/invoice-c/customers/import') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                        <Users className="w-5 h-5" /><span>Customers List</span>
                      </Link>
                  )}
                    </>
                  )}
                </>
              )}

              {/* PRICELIST Section */}
              {(checkAccess('Import Pricelist') || checkAccess('Pricelist') || checkAccess('Special Rate') || checkAccess('Zone') || checkAccess('Master Station') || checkAccess('Fuel Surcharge')) && (
                <>
                  {sectionButton('pricelist', 'Pricelist')}
                  {activeSection === 'pricelist' && (
                    <>
                  {checkAccess('Import Pricelist') && (
                      <Link to="/invoice-c/pricelist/import" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/pricelist/import') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                        <DollarSign className="w-5 h-5" /><span>Import Pricelist</span>
                      </Link>
                  )}
                  {checkAccess('Pricelist') && (
                      <Link to="/invoice-c/pricelist/list" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/pricelist/list') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                        <List className="w-5 h-5" /><span>Pricelist</span>
                      </Link>
                  )}
                  {checkAccess('Special Rate') && (
                      <Link to="/invoice-c/pricelist/special" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/pricelist/special') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                        <Star className="w-5 h-5 text-yellow-500" /><span>Special Rate</span>
                      </Link>
                  )}
                  {checkAccess('Zone') && (
                      <Link to="/invoice-c/pricelist/zone" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/pricelist/zone') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                        <MapPin className="w-5 h-5" /><span>Zone</span>
                      </Link>
                  )}
                  {checkAccess('Master Station') && (
                      <Link to="/invoice-c/stations" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/stations') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                        <Globe className="w-5 h-5" /><span>Master Station</span>
                      </Link>
                  )}
                  {checkAccess('Fuel Surcharge') && (
                      <Link to="/invoice-c/fsc-settings" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/fsc-settings') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                        <Flame className="w-5 h-5 text-orange-500" /><span>Fuel Surcharge</span>
                      </Link>
                  )}
                    </>
                  )}
                </>
              )}

              {/* SALES Section */}
              {/* SALES Section */}
              {checkAccess('Quotation') && (
                <>
                  {sectionButton('sales', 'Sales')}
                  {activeSection === 'sales' && (
                    <>
                  <Link to="/invoice-c/sales/quotation" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${isActive('/invoice-c/sales/quotation') ? 'bg-gradient-to-r from-primary to-primary/90 text-white shadow-lg shadow-primary/25' : 'text-foreground hover:bg-muted/60 hover:translate-x-1'}`}>
                    <TrendingUp className="w-5 h-5" /><span>Quotation</span>
                  </Link>
                    </>
                  )}
                </>
              )}
            </nav>

            {/* Tombol Bawah (Portal & Logout) */}
            <div className="pt-4 border-t border-border space-y-2">
              <Button
                onClick={() => navigate('/portal')}
                variant="outline"
                className="w-full justify-start text-slate-700 border-slate-200 hover:bg-slate-100 rounded-xl font-bold"
              >
                <LayoutDashboard className="w-5 h-5 mr-3 text-slate-500" />
                Kembali ke Portal
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10 rounded-xl font-bold"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 pt-16 md:pt-4 md:p-8 h-screen overflow-y-auto relative z-10 w-full md:w-auto">
          <Outlet />
        </main>
      </div>
      
      {/* Tombol Ganti Password Mengambang */}
      <button 
        onClick={() => navigate('/change-password')}
        className="fixed bottom-6 right-6 bg-slate-800 hover:bg-black text-white p-3 rounded-full shadow-2xl transition hover:scale-110 z-50 group flex items-center gap-2 overflow-hidden"
      >
          <ShieldCheck size={20} />
          <span className="max-w-0 opacity-0 group-hover:max-w-xs group-hover:opacity-100 transition-all duration-300 font-bold text-sm overflow-hidden whitespace-nowrap">Ganti Password</span>
      </button>
      
    </div>
  );
};

export default Layout;